/**
 * Maps API validation field errors to user-friendly messages for creator registration.
 * Replaces the generic "The user scheme is invalid." with specific, actionable messages.
 *
 * @param {string} fallbackMessage - Generic message from API (e.g. "The user scheme is invalid.")
 * @param {Array<{ key: string, error: string }>|null|undefined} fields - API fields array
 * @returns {string[]} Array of messages to show as list items in the Registration Failed pop-up
 */
export function getCreatorRegistrationErrorMessage(fallbackMessage, fields) {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return [fallbackMessage || 'Something went wrong. Please try again.'];
  }

  const friendlyMessages = [];

  for (const { key, error } of fields) {
    const normalizedKey = (key || '').toLowerCase().replace(/_/g, '');
    const err = (error || '').toLowerCase();

    // Email: any pattern/format error
    if (normalizedKey === 'email') {
      friendlyMessages.push('Please enter a valid email address.');
      continue;
    }

    // Name fields and username: pattern / invalid / special characters
    const isNameOrUsername = ['firstname', 'middlename', 'lastname', 'username'].includes(normalizedKey);
    if (isNameOrUsername) {
      friendlyMessages.push('Special characters are not allowed in name fields or usernames.');
      continue;
    }

    // Password
    if (normalizedKey.includes('password')) {
      friendlyMessages.push('Password must be at least 6 characters with uppercase, lowercase, number, and special character.');
      continue;
    }

    // Date of birth
    if (normalizedKey.includes('dob') || normalizedKey.includes('date') || normalizedKey.includes('birth')) {
      friendlyMessages.push('Please enter a valid date of birth.');
      continue;
    }

    // Phone
    if (normalizedKey.includes('phone')) {
      friendlyMessages.push('Please enter a valid phone number.');
      continue;
    }

    // Generic
    if (err.includes('required') || err.includes('mandatory')) {
      friendlyMessages.push(`Please fill in ${(key || '').replace(/_/g, ' ')}.`);
    } else {
      friendlyMessages.push('Special characters are not allowed in name fields or usernames.');
    }
  }

  // Deduplicate and return as array for ul/li display
  return [...new Set(friendlyMessages)];
}
